import { useLayoutEffect, useRef } from 'react';

import { Grid, Line } from '@react-three/drei';
import * as THREE from 'three';

interface CustomGridProps {
  /** Used as fade radius — grid lines fade out beyond this distance from the camera. */
  size?: number;
  step?: number;
  darkGrayColor?: string;
  orangeColor?: string;
}

/** 1 cm below z=0 — keeps coplanar geometry from z-fighting with the grid. */
const GRID_Z_OFFSET = -0.01;

/** Multiplier applied to the grid shader's line alpha (drei has no opacity prop). */
const CELL_LINE_ALPHA_BOOST = 2;

const applyGridMaterialSettings = (material: THREE.Material | THREE.Material[]) => {
  const materials = Array.isArray(material) ? material : [material];
  materials.forEach((mat) => {
    if (!(mat instanceof THREE.ShaderMaterial)) return;

    mat.depthWrite = false;
    mat.polygonOffset = true;
    mat.polygonOffsetFactor = -4;
    mat.polygonOffsetUnits = -4;

    mat.onBeforeCompile = (shader) => {
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <tonemapping_fragment>',
        `gl_FragColor.a = min(gl_FragColor.a * ${CELL_LINE_ALPHA_BOOST.toFixed(1)}, 1.0);
         #include <tonemapping_fragment>`,
      );
    };
    mat.customProgramCacheKey = () => 'custom-grid-cell-opacity';
    mat.needsUpdate = true;
  });
};

const CustomGrid = ({ size = 5000, step = 1, darkGrayColor = '#333333', orangeColor = '#4FD12E' }: CustomGridProps) => {
  const gridRef = useRef<THREE.Mesh>(null);

  useLayoutEffect(() => {
    const material = gridRef.current?.material;
    if (!material) return;
    applyGridMaterialSettings(material);
  }, []);

  const axisLineProps = {
    lineWidth: 1.5,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
    renderOrder: -1,
    userData: { isGrid: true },
  } as const;

  return (
    <>
      <Grid
        ref={gridRef}
        name="CustomGrid"
        userData={{ isGrid: true }}
        position={[0, 0, GRID_Z_OFFSET]}
        // Default drei Grid lies on XZ (Y-up floor); rotate to the workspace XY plane (Z-forward).
        rotation={[Math.PI / 2, 0, 0]}
        side={THREE.DoubleSide}
        infiniteGrid
        followCamera
        cellSize={step}
        cellColor={darkGrayColor}
        cellThickness={1}
        // Disable the section pass — axis highlighting is handled by explicit lines below.
        sectionSize={step}
        sectionColor={darkGrayColor}
        sectionThickness={0}
        fadeDistance={size}
        fadeStrength={1}
        renderOrder={-1}
      />

      {/* World-fixed origin axes — separate from the fading cell grid shader. */}
      <Line
        points={[
          [-size, 0, GRID_Z_OFFSET],
          [size, 0, GRID_Z_OFFSET],
        ]}
        color={orangeColor}
        {...axisLineProps}
      />
      <Line
        points={[
          [0, -size, GRID_Z_OFFSET],
          [0, size, GRID_Z_OFFSET],
        ]}
        color={orangeColor}
        {...axisLineProps}
      />
    </>
  );
};

export default CustomGrid;
